import React from "react";
import { DateTime } from "luxon";
import { CRMColors } from "../../../models/CRMColors";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { Background } from "../../BuildingBlocks/Background";
import { FontQuicksand } from "../../BuildingBlocks/FontQuicksand";
import { FontSize } from "../../BuildingBlocks/FontSize";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { CRMIcon } from "../../CRMIcon/CRMIcon";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { Uppercase } from "../../Uppercase/Uppercase";
import { WeightBold } from "../../WeightBold/WeightBold";
import { CRMFontSizes } from "../../../models/CRMFontSizes";
import { TCaseStatus } from "../../../../../domain/codecs/Cases";

export const AUCTION_PACK_WARNING_DAYS = 7;

type TCRMCaseAuctionPackBannerProps = {
    caseStatus: TCaseStatus;
    auctionDate: string | null;
    auctionPackSent: string | null;
};

type TAuctionPackWarning = {
    message: string;
    isOverdue: boolean;
};

const toUtcDay = (iso: string): DateTime => DateTime.fromISO(iso, { zone: "utc" }).startOf("day");

const describeAuctionDay = (auctionDate: string, days: number): string => {
    const formatted = toUtcDay(auctionDate).toFormat("d LLL yyyy");

    return days < -1 ? `The auction was on ${formatted}, ${Math.abs(days)} days ago.`
        : days === -1 ? `The auction was yesterday, ${formatted}.`
        : days === 0 ? `The auction is today, ${formatted}.`
        : days === 1 ? `The auction is tomorrow, ${formatted}.`
        : `The auction is in ${days} days, on ${formatted}.`;
};

const isStillWorthChasing = (caseStatus: TCaseStatus): boolean =>
    caseStatus === "property_report"
    || caseStatus === "quote"
    || caseStatus === "instructed"
    || caseStatus === "ready_for_exchange"
;

export const getAuctionPackWarning = (
    caseStatus: TCaseStatus,
    auctionDate: string | null,
    auctionPackSent: string | null,
    today: DateTime,
): TAuctionPackWarning | null => {
    if (auctionDate === null || auctionDate === "" || auctionPackSent !== null || !isStillWorthChasing(caseStatus)) {
        return null;
    }

    const auctionDay = toUtcDay(auctionDate);

    if (!auctionDay.isValid) {
        return null;
    }

    const days = Math.round(auctionDay.diff(today.startOf("day"), "days").days);

    if (days >= AUCTION_PACK_WARNING_DAYS) {
        return null;
    }

    return {
        message: describeAuctionDay(auctionDate, days),
        isOverdue: days <= 0,
    };
};

export const CRMCaseAuctionPackBanner = (props: TCRMCaseAuctionPackBannerProps): JSX.Element => {
    const warning = getAuctionPackWarning(props.caseStatus, props.auctionDate, props.auctionPackSent, DateTime.utc());

    if (warning === null) {
        return <></>;
    }

    const colors = warning.isOverdue
        ? { background: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_12, border: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_10 }
        : { background: CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_12, border: CRMColors.HIGHLIGHTS_PERTINENT_YELLOW_10 };

    return (
        <Background
            color={colors.background}
            borderDefinition={`1px solid ${colors.border}`}
            borderRadius="5px"
            padding={CRMSpacing.MEDIUM}
        >
            <SpacingColumn spacing={CRMSpacing.SMALL}>
                <SpacingRow spacing={CRMSpacing.TINY}>
                    <CRMIcon
                        iconName="legal-gavel"
                        size="small"
                        colour="neutral-ink"
                    />
                    <FontQuicksand>
                        <FontSize size={CRMFontSizes.SMALL} lineHeight="1.2">
                            <Uppercase>
                                <WeightBold>Auction pack not sent</WeightBold>
                            </Uppercase>
                        </FontSize>
                    </FontQuicksand>
                </SpacingRow>

                <CRMParagraph>
                    {warning.message} Mark the pack as sent in the Auction section once it has gone out.
                </CRMParagraph>
            </SpacingColumn>
        </Background>
    );
};
